import React, { useState, useMemo, useEffect } from 'react';
import { useGames } from './hooks/useGames';
import { useClaimed } from './hooks/useClaimed';
import { useWishlist } from './hooks/useWishlist';
import { Header } from './containers/Header';
import { PlatformFilter } from './containers/PlatformFilter';
import { GameGrid } from './containers/GameGrid';
import { PLATFORMS, ALL_PLATFORMS } from './constants/platforms';
import styles from './App.module.css';

export const SAVED_TAB = '__saved__';

function App() {
  const { games, loading, backgroundRefresh, error, lastUpdated, isDemoMode } = useGames();
  const { isClaimed, toggleClaimed } = useClaimed();
  const { isWishlisted, toggleWishlist, wishlistSize } = useWishlist();
  const [activePlatform, setActivePlatform] = useState(ALL_PLATFORMS);
  const [showThanks, setShowThanks] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('donated') === '1';
  });

  useEffect(() => {
    if (showThanks) {
      window.history.replaceState({}, '', window.location.pathname);
      const t = setTimeout(() => setShowThanks(false), 6000);
      return () => clearTimeout(t);
    }
  }, [showThanks]);

  useEffect(() => {
    if (loading) {
      document.title = 'GameDrop — Loading…';
    }else {
      document.title = 'GameDrop — Free Games Tracker';
    }
  }, [games.length, loading]);

  const handlePlatformChange = (platform) => {
    setActivePlatform(platform);
  };

  const filteredGames = useMemo(() => {
    if (activePlatform === SAVED_TAB) return games.filter(g => isWishlisted(g.id));
    if (activePlatform === ALL_PLATFORMS) return games;
    return games.filter(g => g.platform === activePlatform);
  }, [games, activePlatform, isWishlisted]);

  const dealCounts = useMemo(() => {
    const counts = { [ALL_PLATFORMS]: games.filter(g => g.type !== 'coming-soon').length };
    Object.keys(PLATFORMS).forEach(key => {
      counts[key] = games.filter(g => g.platform === key && g.type !== 'coming-soon').length;
    });
    counts[SAVED_TAB] = wishlistSize;
    return counts;
  }, [games, wishlistSize]);

  return (
    <div className={styles.app}>
      {showThanks && (
        <div className={styles.thanksBanner}>
          ♥ Thank you for supporting GameDrop!
        </div>
      )}
      <Header />
      <PlatformFilter
        activePlatform={activePlatform}
        onPlatformChange={handlePlatformChange}
        dealCounts={dealCounts}
      />

      <main className={styles.main}>
        <GameGrid
          games={filteredGames}
          loading={loading}
          error={error}
          activePlatform={activePlatform}
          isDemoMode={isDemoMode}
          isClaimed={isClaimed}
          toggleClaimed={toggleClaimed}
          isWishlisted={isWishlisted}
          toggleWishlist={toggleWishlist}
        />
      </main>
      <footer className={styles.footer}>
        <div className={styles.footerLeft}>
          <strong>GameDrop  — Free Games Tracker</strong>
        </div>
        {lastUpdated && (
          <div className={styles.lastUpdated}>
            <span className={`${styles.dot} ${backgroundRefresh ? styles.dotRefreshing : ''}`} />
            {backgroundRefresh
              ? 'Updating\u2026'
              : `Updated ${formatLastUpdated(lastUpdated)}`}
          </div>
        )}
        <div className={styles.footerRight}>
          Data from Epic · Steam · GOG · Itch.io
        </div>
      </footer>
    </div>
  );
}

function formatLastUpdated(date) {
  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `today, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `yesterday, ${time}`;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}

export default App;
